import { matchPath } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';
import type { IconType } from 'react-icons';
import {
  MdCameraAlt,
  MdCollections,
  MdOutlineCameraAlt,
  MdOutlineCollections,
  MdOutlineSearch,
  MdSearch,
} from 'react-icons/md';
import DeckIcon from '../../images/deck-icon.svg?react';
import DiceIcon from '../../images/dice-icon.svg?react';

export type RouteIcon = IconType | ComponentType<SVGProps<SVGSVGElement>>;

export type RouteMatchFn = (pathname: string) => boolean;

export interface AppRouteMetadata {
  id: string;
  to: string;
  label: string;
  navTitle?: string;
  match: RouteMatchFn;
  desktopNav?: boolean;
  mobileTab?: {
    icon: RouteIcon;
    iconActive: RouteIcon;
    prefetch: () => Promise<unknown>;
  };
  showMobileTabs?: boolean;
  hideFooterOnMobile?: boolean;
  fullBleedHero?: boolean;
  fullBleedBrowse?: boolean;
}

/** Catalog browse pages that sit under the Cards tab chrome (not stack tips). */
export function isCardsCatalogBrowseRoute(pathname: string): boolean {
  return (
    pathname === '/sets' ||
    pathname.startsWith('/sets/') ||
    pathname === '/legends' ||
    pathname.startsWith('/legends/') ||
    pathname === '/errata'
  );
}

export const APP_ROUTE_METADATA: AppRouteMetadata[] = [
  {
    id: 'home',
    to: '/',
    label: 'Home',
    match: (pathname) => pathname === '/',
    showMobileTabs: true,
    hideFooterOnMobile: true,
  },
  {
    id: 'cards',
    to: '/search',
    label: 'Cards',
    navTitle: 'Cards',
    match: (pathname) =>
      pathname === '/search' || pathname.startsWith('/cards') || isCardsCatalogBrowseRoute(pathname),
    desktopNav: true,
    showMobileTabs: true,
    hideFooterOnMobile: true,
    fullBleedBrowse: true,
    mobileTab: {
      icon: MdOutlineSearch,
      iconActive: MdSearch,
      prefetch: () => import('../../views/SearchPage'),
    },
  },
  {
    id: 'collection',
    to: '/collection',
    label: 'Collection',
    navTitle: 'Collection',
    match: (pathname) =>
      pathname === '/collection' ||
      pathname.startsWith('/collection/') ||
      pathname === '/features/collection',
    desktopNav: true,
    showMobileTabs: true,
    hideFooterOnMobile: true,
    fullBleedBrowse: true,
    mobileTab: {
      icon: MdOutlineCollections,
      iconActive: MdCollections,
      prefetch: () => import('../../views/CollectionPage'),
    },
  },
  {
    id: 'decks',
    to: '/decks',
    label: 'Decks',
    navTitle: 'Decks',
    match: (pathname) => pathname === '/decks' || pathname.startsWith('/decks/'),
    desktopNav: true,
    showMobileTabs: true,
    hideFooterOnMobile: true,
    mobileTab: {
      icon: DeckIcon,
      iconActive: DeckIcon,
      prefetch: () => import('../../views/DecksPage'),
    },
  },
  {
    id: 'play',
    to: '/play',
    label: 'Play',
    navTitle: 'Play',
    match: (pathname) =>
      pathname === '/play' ||
      pathname.startsWith('/play/') ||
      pathname.startsWith('/matches') ||
      pathname.startsWith('/events') ||
      pathname === '/features/match-tracker',
    desktopNav: true,
    showMobileTabs: true,
    hideFooterOnMobile: true,
    mobileTab: {
      icon: DiceIcon,
      iconActive: DiceIcon,
      prefetch: () => import('../../views/PlayHomePage'),
    },
  },
  {
    id: 'teams',
    to: '/team',
    label: 'Team',
    navTitle: 'Teams',
    match: (pathname) =>
      pathname === '/team' || pathname.startsWith('/teams/') || pathname === '/features/teams',
    desktopNav: true,
    // Still omitted from mobileTab — the bottom bar is already Cards, Collection,
    // Scan, Decks, Play. Teams stays in the account sheet on small screens.
    showMobileTabs: true,
    hideFooterOnMobile: true,
    fullBleedHero: true,
  },
  {
    id: 'proxy',
    to: '/card-print',
    label: 'Proxy',
    navTitle: 'Proxy',
    match: (pathname) => pathname === '/card-print',
    desktopNav: true,
    showMobileTabs: true,
  },
  {
    id: 'riftboundle',
    to: '/riftboundle',
    label: 'Riftboundle',
    navTitle: 'Riftboundle',
    match: (pathname) => pathname === '/riftboundle',
    desktopNav: true,
  },
];

export const MOBILE_SCAN_TAB = {
  to: '/collection/scan',
  label: 'Scan',
  icon: MdOutlineCameraAlt,
  iconActive: MdCameraAlt,
  prefetch: () => import('../../views/CollectionScanPage'),
};

export function routeMetadataFor(pathname: string): AppRouteMetadata | undefined {
  return APP_ROUTE_METADATA.find((route) => route.match(pathname));
}

export function desktopNavRoutes(): AppRouteMetadata[] {
  return APP_ROUTE_METADATA.filter((route) => route.desktopNav);
}

export function mobileTabRoutes(): AppRouteMetadata[] {
  return APP_ROUTE_METADATA.filter((route) => route.mobileTab);
}

export function getRouteNavTitle(pathname: string): string | undefined {
  if (pathname === '/') return undefined;
  if (pathname === '/collection/binders' || pathname.startsWith('/collection/binders/')) return 'Binders';
  if (pathname === '/decks/my-decks') return 'My Decks';
  if (pathname === '/matches' || pathname.startsWith('/matches/')) return 'Match History';
  if (pathname === '/events') return 'Events';
  if (pathname.startsWith('/events/')) return 'Event Recap';
  if (pathname.startsWith('/teams/')) return 'Team';
  if (pathname.startsWith('/players/')) return 'Profile';
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return 'Account';
  if (pathname === '/sets' || pathname.startsWith('/sets/')) return 'Sets';
  if (pathname === '/legends' || pathname.startsWith('/legends/')) return 'Legends';
  if (pathname === '/about') return 'About';
  if (pathname === '/terms') return 'Terms';
  if (pathname === '/privacy') return 'Privacy';
  if (pathname === '/errata') return 'Errata';
  return routeMetadataFor(pathname)?.navTitle;
}

export async function prefetchRouteDestination(
  rootPrefetch: () => Promise<unknown>,
  dest: string,
): Promise<void> {
  const jobs: Promise<unknown>[] = [rootPrefetch()];
  if (dest.startsWith('/cards')) {
    jobs.push(import('../../views/SearchPage'));
  }
  if (dest.startsWith('/collection/binders/')) {
    jobs.push(import('../../views/BinderPage'));
  } else if (dest.startsWith('/collection/binders')) {
    jobs.push(import('../../views/BindersPage'));
  }
  if (dest === '/decks/new' || dest.endsWith('/edit')) {
    jobs.push(import('../../views/DeckBuilderPage'));
  } else if (
    dest.startsWith('/decks/') &&
    dest !== '/decks/my-decks'
  ) {
    if (dest.endsWith('/performance')) {
      jobs.push(import('../../views/DeckPerformancePage'));
    } else {
      jobs.push(import('../../views/DeckDetailPage'));
    }
  }
  if (dest.startsWith('/matches/')) {
    jobs.push(import('../../views/MatchResultDetailPage'));
  } else if (dest === '/matches') {
    jobs.push(import('../../views/MatchHistoryPage'));
  }
  if (dest === '/play/match') {
    jobs.push(import('../../views/PlayPage'));
  } else if (dest.startsWith('/play/') && dest !== '/play/join') {
    jobs.push(import('../../views/MatchSessionPage'));
  }
  if (dest.startsWith('/events/')) {
    jobs.push(import('../../views/EventRecapPage'));
  } else if (dest === '/events') {
    jobs.push(import('../../views/EventsPage'));
  }
  if (dest.startsWith('/teams/')) {
    jobs.push(import('../../features/teams/pages/TeamProfilePage'));
  }
  await Promise.all(jobs);
}

export function isMobileFullBleedHeroRoute(pathname: string): boolean {
  const isDeckViewHero =
    pathname !== '/decks/new' &&
    pathname !== '/decks/my-decks' &&
    !matchPath('/decks/:deckId/edit', pathname) &&
    Boolean(
      matchPath('/decks/:deckId', pathname) ||
        matchPath('/decks/:deckId/versions', pathname) ||
        matchPath('/decks/:deckId/performance', pathname),
    );

  return Boolean(
    routeMetadataFor(pathname)?.fullBleedHero ||
      matchPath('/teams/:slug', pathname) ||
      matchPath('/players/:slug', pathname) ||
      isDeckViewHero,
  );
}

export function isMobileFullBleedBrowseRoute(pathname: string): boolean {
  return Boolean(
    routeMetadataFor(pathname)?.fullBleedBrowse ||
      matchPath('/collection/sets/:setSlug', pathname) ||
      matchPath('/collections/:slug', pathname) ||
      matchPath('/collections/:slug/sets/:setSlug', pathname),
  );
}
